import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Shield, AlertTriangle, Eye, Clock, Fingerprint as FpIcon, FileText, BarChart3, TrendingUp, Crosshair, Users, Timer, MousePointerClick, MapPin, MapPinOff, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { StatCard } from '../components/StatCard';
import { Globe3D, type ThreatPoint } from '../components/Globe3D';
import { Starfield } from '../components/Starfield';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { useDashboardStats, useThreatFeed } from '../lib/hooks';
import { useAuth } from '../lib/auth-context';
import { ThreatBadge } from '../components/ThreatBadge';
import { FleetStatusBanner } from '../components/FleetStatusBanner';
import {
  ENGAGEMENT_VOCAB,
  OVERVIEW_TILE_HELP,
  resolveDeepCallbacks,
} from '../lib/engagement-vocab';
import { geoApproxNote, resolveThreatGeo } from '../lib/geo';
import type { ThreatFeedItem } from '../lib/api';

export function Overview() {
  const { isConnected, activeTenantId, tenant } = useAuth();
  const [chartType, setChartType] = React.useState<'bar' | 'area'>('bar');
  const [timePeriod, setTimePeriod] = React.useState<'24h' | '7d' | '30d' | 'all'>('24h');
  const [selectedThreatId, setSelectedThreatId] = React.useState<string | null>(null);
  const [focusToken, setFocusToken] = React.useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  
  // Convert time period to hours (undefined = all time)
  const hours = timePeriod === '24h' ? 24 : timePeriod === '7d' ? 168 : timePeriod === '30d' ? 720 : undefined;
  
  const { data: stats, isLoading: statsLoading } = useDashboardStats(hours);
  const { data: feed, isLoading: feedLoading } = useThreatFeed();
  const tenantLabel = tenant?.name || activeTenantId?.slice(0, 8) || 'project';

  // Reset selection when switching tenants
  useEffect(() => {
    setSelectedThreatId(null);
  }, [activeTenantId]);

  const recentThreats = useMemo(
    () => (feed?.threats ?? []).slice(0, 10),
    [feed?.threats],
  );

  const threatGeoById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof resolveThreatGeo>>();
    for (const t of feed?.threats ?? []) {
      map.set(t.id, resolveThreatGeo(t.sourceCity, t.sourceCountry));
    }
    return map;
  }, [feed?.threats]);

  const globePoints: ThreatPoint[] = useMemo(() => {
    return (feed?.threats ?? [])
      .map((t) => {
        const geo = threatGeoById.get(t.id) ?? resolveThreatGeo(t.sourceCity, t.sourceCountry);
        if (!geo.coords) return null;
        return {
          id: t.id,
          lat: geo.coords.lat,
          lng: geo.coords.lng,
          level: (t.level === 'none' ? 'low' : t.level) as ThreatPoint['level'],
          count: t.requestCount || 1,
          city: t.sourceCity || (geo.precision === 'country' ? t.sourceCountry : undefined),
          country: t.sourceCountry,
          label: t.sourceIp,
        } satisfies ThreatPoint;
      })
      .filter((p): p is ThreatPoint => p !== null);
  }, [feed?.threats, threatGeoById]);

  const selectedThreat: ThreatFeedItem | null = useMemo(() => {
    if (!selectedThreatId) return null;
    return (feed?.threats ?? []).find((t) => t.id === selectedThreatId) ?? null;
  }, [feed?.threats, selectedThreatId]);

  /**
   * Focus a threat on the globe, or clear if the same id is already selected.
   * Re-click toggles off so the full multi-arc field returns.
   */
  const focusThreat = useCallback((id: string, opts?: { fromGlobe?: boolean }) => {
    setSelectedThreatId((prev) => {
      const next = prev === id ? null : id;
      if (next != null) {
        // Scroll after selecting (not when clearing)
        requestAnimationFrame(() => {
          if (opts?.fromGlobe) {
            rowRefs.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          } else {
            document.getElementById('globe-overview')?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
            });
          }
        });
      }
      return next;
    });
    setFocusToken((n) => n + 1);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedThreatId(null);
    setFocusToken((n) => n + 1);
  }, []);

  // Sanitize timeline data - replace null/undefined with 0
  let timelineData = stats?.timeline?.map(t => ({
    ...t,
    sessions: t.sessions ?? 0,
    threats: t.threats ?? 0,
    deceptions: t.deceptions ?? 0,
    fingerprints: t.fingerprints ?? 0,
  })) || [];

  // If timeline has more than 168 points (7 days hourly), sample it down
  const sanitizedTimeline = timelineData.length > 168
    ? timelineData.filter((_, i) => i % Math.ceil(timelineData.length / 168) === 0)
    : timelineData;

  /**
   * Build a left→right sparkline series ending at "now".
   * Backend 24h buckets are calendar hours "00:00"…"23:00" (not rolling),
   * so we rotate so the current hour is last — avoids bogus cliffs from midnight wrap.
   */
  const sparkSeries = (key: 'sessions' | 'threats' | 'deceptions' | 'fingerprints'): number[] => {
    if (!sanitizedTimeline.length) return [];
    const isHourLabel = /^\d{2}:00$/.test(String(sanitizedTimeline[0]?.time || ''));
    if (isHourLabel) {
      const byHour = new Map<number, number>();
      for (const t of sanitizedTimeline) {
        const h = parseInt(String(t.time).slice(0, 2), 10);
        if (!Number.isNaN(h)) byHour.set(h, Number(t[key]) || 0);
      }
      const nowH = new Date().getHours();
      const out: number[] = [];
      for (let i = 23; i >= 0; i--) {
        const h = (nowH - i + 24) % 24;
        out.push(byHour.get(h) ?? 0);
      }
      return out;
    }
    return sanitizedTimeline.map((t) => Number(t[key]) || 0);
  };

  const sessionsSparkline = sparkSeries('sessions');
  const threatsSparkline = sparkSeries('threats');
  const deceptionsSparkline = sparkSeries('deceptions');
  // Hop-based canonical count (engagement_hops), legacy engaged=1 only as fallback.
  const deepCallbacks = resolveDeepCallbacks(stats);
  const fingerprintsSparkline = sparkSeries('fingerprints');

  if (!isConnected) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold text-white mb-4">Threat Overview</h2>
        <div className="bg-sg-card border border-sg-border rounded-xl p-12 text-center">
          <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-500">Connect to the control plane to view threat data</p>
          <p className="text-gray-600 text-sm mt-2">Go to Settings to configure your API endpoint</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[100vw] overflow-x-hidden">
      <div className="mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-bold text-white">Threat Overview</h2>
        <p className="text-gray-400 mt-1 text-sm sm:text-base">
          Real-time security posture for{' '}
          <span className="text-white font-medium">{tenantLabel}</span>
          {activeTenantId && (
            <span className="text-gray-600 text-xs font-mono ml-2 hidden sm:inline">
              {activeTenantId.slice(0, 8)}…
            </span>
          )}
        </p>
      </div>

      {/* Fleet liveness + integrity — first thing on load; silent failures surface here */}
      <FleetStatusBanner />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <StatCard 
          label="Active Sessions" 
          hint={OVERVIEW_TILE_HELP.activeSessions}
          value={statsLoading ? '—' : (stats?.activeSessions ?? 0).toLocaleString()} 
          icon={<Shield className="w-4 h-4" />} 
          color="cyan"
          sparklineData={sessionsSparkline}
        />
        <Link to="/actors" className="block min-w-0">
          <StatCard
            label="Actors"
            hint={OVERVIEW_TILE_HELP.actors}
            value={statsLoading ? '—' : (stats?.actorClusters ?? 0).toLocaleString()}
            icon={<Users className="w-4 h-4" />}
            color="teal"
            meter={
              statsLoading
                ? 0
                : Math.min(1, Math.log10((stats?.actorClusters ?? 0) + 1) / 3)
            }
          />
        </Link>
        <StatCard 
          label="Active Threats" 
          hint={OVERVIEW_TILE_HELP.activeThreats}
          value={statsLoading ? '—' : (stats?.activeThreats ?? 0)} 
          icon={<AlertTriangle className="w-4 h-4" />} 
          color="purple"
          sparklineData={threatsSparkline}
        />
        <StatCard 
          label={ENGAGEMENT_VOCAB.decoysServed.label}
          hint={ENGAGEMENT_VOCAB.decoysServed.hint}
          value={statsLoading ? '—' : (stats?.deceptionsServed ?? 0)} 
          icon={<Eye className="w-4 h-4" />} 
          color="cyan"
          sparklineData={deceptionsSparkline}
        />
        <StatCard 
          label={ENGAGEMENT_VOCAB.timeBurned.label}
          hint={ENGAGEMENT_VOCAB.timeBurned.hint}
          value={statsLoading ? '—' : (stats?.deceptionsEngaged ?? 0)} 
          icon={<Timer className="w-4 h-4" />} 
          color="amber"
          sparklineData={deceptionsSparkline}
        />
        <StatCard 
          label={ENGAGEMENT_VOCAB.timeWasted.label}
          hint={ENGAGEMENT_VOCAB.timeWasted.hint}
          value={statsLoading ? '—' : `${Math.round((stats?.timeWastedMs ?? 0) / 1000)}s`} 
          icon={<Clock className="w-4 h-4" />} 
          color="green"
          sparklineData={deceptionsSparkline}
        />
        <StatCard
          label={ENGAGEMENT_VOCAB.deepCallbacks.label}
          hint={
            (stats?.maxEngagementDepth ?? 0) > 0
              ? `${ENGAGEMENT_VOCAB.deepCallbacks.hint} Deepest stage reached: ${stats?.maxEngagementDepth}.`
              : ENGAGEMENT_VOCAB.deepCallbacks.hint
          }
          value={statsLoading ? '—' : deepCallbacks}
          icon={<MousePointerClick className="w-4 h-4" />}
          color="purple"
          meter={
            statsLoading
              ? 0
              : Math.min(1, deepCallbacks > 0 ? 0.2 + Math.min(0.8, deepCallbacks / 20) : 0)
          }
        />
        <Link to="/canaries" className="block min-w-0">
          <StatCard
            label="Canary Hits"
            hint={OVERVIEW_TILE_HELP.canaryHits}
            value={statsLoading ? '—' : (stats?.canaryHits ?? 0)}
            icon={<Crosshair className="w-4 h-4" />}
            color="purple"
            meter={
              statsLoading
                ? 0
                : Math.min(1, ((stats?.canaryHits ?? 0) > 0
                    ? 0.25 + Math.min(0.75, (stats?.canaryHits ?? 0) / 10)
                    : 0))
            }
          />
        </Link>
        <StatCard 
          label="Fingerprints" 
          hint={OVERVIEW_TILE_HELP.fingerprints}
          value={statsLoading ? '—' : (stats?.fingerprintsTracked ?? 0)} 
          icon={<FpIcon className="w-4 h-4" />} 
          color="teal"
          sparklineData={fingerprintsSparkline}
        />
        <StatCard 
          label="Avg Confidence" 
          hint={OVERVIEW_TILE_HELP.avgConfidence}
          value={statsLoading ? '—' : `${stats?.avgConfidence ?? 0}%`} 
          icon={<FileText className="w-4 h-4" />} 
          color="cyan"
          meter={statsLoading ? 0 : Math.min(1, (stats?.avgConfidence ?? 0) / 100)}
        />
      </div>

      {/* Split row: Map (left) and Recent Threats (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
        {/* 3D Globe */}
        <div className="bg-white/[0.015] border border-white/[0.04] rounded-2xl overflow-hidden backdrop-blur-sm">
          <div className="p-4 sm:p-6 border-b border-white/[0.05] flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wider">Attack origins</h3>
              <p className="text-[11px] text-gray-500 mt-0.5 truncate">
                {globePoints.length} mapped for this project · click list or hex to focus
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {selectedThreatId && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="p-2 hover:bg-white/5 rounded-lg transition-colors text-gray-400 hover:text-white"
                  title="Clear selection"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  const el = document.getElementById('globe-overview');
                  if (el) {
                    if (document.fullscreenElement) {
                      document.exitFullscreen();
                    } else {
                      el.requestFullscreen();
                    }
                  }
                }}
                className="p-2 hover:bg-white/5 rounded-lg transition-colors text-gray-400 hover:text-white"
                title="Toggle fullscreen"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              </button>
            </div>
          </div>
          <div id="globe-overview" className="h-[240px] sm:h-[320px] lg:h-[400px]" style={{ position: 'relative', background: '#000' }}>
            <Starfield />
            <style>{`
              #globe-overview:fullscreen {
                height: 100vh !important;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #000 !important;
              }
            `}</style>
            <Globe3D
              points={globePoints}
              selectedId={selectedThreatId}
              focusToken={focusToken}
              onPointClick={(p) => focusThreat(p.id, { fromGlobe: true })}
              centerLat={30}
              centerLng={-40}
              className="w-full h-full"
            />
            {/* Selection HUD overlay */}
            {selectedThreat && (
              <div className="absolute left-3 right-3 bottom-3 sm:left-4 sm:right-auto sm:bottom-4 sm:max-w-sm z-10 pointer-events-none">
                <div className="pointer-events-auto rounded-xl border border-indigo-500/40 bg-black/80 backdrop-blur-md px-3 py-2.5 shadow-lg shadow-indigo-500/10">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <ThreatBadge level={selectedThreat.level} />
                        <span className="text-xs font-mono text-white truncate">{selectedThreat.sourceIp}</span>
                      </div>
                      <div className="text-[11px] text-gray-400 truncate">
                        {[selectedThreat.sourceCity, selectedThreat.sourceCountry].filter(Boolean).join(' · ') || 'Location approximate'}
                        {threatGeoById.get(selectedThreat.id)?.precision === 'country' ? ' · approx. pin' : ''}
                      </div>
                      {selectedThreat.techniques[0] && (
                        <div className="text-[11px] text-indigo-300 mt-1 truncate">
                          {selectedThreat.techniques.slice(0, 2).join(' · ')}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="p-1 rounded-md text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
                      aria-label="Clear map selection"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Recent Threats — keyed by tenant so React never reuses wrong list */}
        <div
          key={activeTenantId || 'no-tenant'}
          className="bg-white/[0.015] border border-white/[0.04] rounded-2xl p-4 sm:p-6 backdrop-blur-sm flex flex-col"
        >
          <div className="flex items-center justify-between gap-2 mb-4 sm:mb-6">
            <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wider">Recent Threats</h3>
            <span className="text-[11px] text-gray-500 hidden sm:inline">Click to locate on globe</span>
          </div>
          {feedLoading && !feed ? (
            <div className="text-gray-400 text-sm">Loading threats for this project…</div>
          ) : recentThreats.length > 0 ? (
            <div
              ref={listRef}
              className="space-y-2 sm:space-y-2.5 max-h-[280px] sm:max-h-[360px] lg:max-h-[400px] overflow-auto pr-1 sm:pr-2"
              role="listbox"
              aria-label="Recent threats"
            >
              {recentThreats.map((t) => {
                const geo = threatGeoById.get(t.id);
                const mappable = geo?.coords != null;
                const selected = selectedThreatId === t.id;
                return (
                  <button
                    key={`${activeTenantId}-${t.id}`}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    disabled={!mappable}
                    ref={(el) => {
                      if (el) rowRefs.current.set(t.id, el);
                      else rowRefs.current.delete(t.id);
                    }}
                    onClick={() => {
                      if (!mappable) return;
                      // Re-click selected row → deselect (full arc field returns)
                      focusThreat(t.id);
                    }}
                    title={
                      mappable
                        ? selected
                          ? `Clear focus on ${t.sourceIp}`
                          : `Focus ${t.sourceIp} on globe`
                        : 'No city/country match — cannot plot on map'
                    }
                    className={[
                      'w-full text-left rounded-xl px-3 py-3 border transition-all duration-200',
                      'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60',
                      selected
                        ? 'border-indigo-500/50 bg-indigo-500/10 shadow-[0_0_0_1px_rgba(99,102,241,0.25)]'
                        : mappable
                          ? 'border-transparent hover:border-white/10 hover:bg-white/[0.03] cursor-pointer'
                          : 'border-transparent opacity-60 cursor-not-allowed',
                    ].join(' ')}
                  >
                    {/* Top row: Badge, IP, Location, map affordance */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                      <ThreatBadge level={t.level} />
                      <span className="text-sm sm:text-base font-mono text-white font-medium break-all">{t.sourceIp}</span>
                      {t.sourceCountry && (
                        <span className="text-xs sm:text-sm text-gray-400">
                          {t.sourceCountry}{t.sourceCity ? ` • ${t.sourceCity}` : ''}
                        </span>
                      )}
                      <span className="ml-auto shrink-0" aria-hidden>
                        {mappable ? (
                          <MapPin
                            className={`w-3.5 h-3.5 transition-colors ${
                              selected ? 'text-indigo-400' : 'text-gray-500'
                            }`}
                          />
                        ) : (
                          <MapPinOff className="w-3.5 h-3.5 text-gray-600" />
                        )}
                      </span>
                    </div>
                    {/* Bottom row: Techniques, Confidence, Time */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ml-0 sm:ml-1">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                        {t.techniques.slice(0, 3).map((tech) => (
                          <span key={tech} className="px-2 py-0.5 sm:px-2.5 sm:py-1 bg-white/[0.06] text-[11px] sm:text-xs text-gray-300 rounded font-medium">{tech}</span>
                        ))}
                      </div>
                      <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm shrink-0">
                        <span className="text-gray-300 font-medium">{t.confidence}%</span>
                        <span className="text-gray-400">{new Date(t.timestamp).toLocaleTimeString()}</span>
                      </div>
                    </div>
                    {selected && geo && geoApproxNote(geo) && (
                      <div className="mt-2 text-[10px] text-amber-400/90">
                        {geoApproxNote(geo)}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-gray-400 text-sm">
              No threats for this project yet. Telemetry is being collected.
            </div>
          )}
        </div>
      </div>

      {/* Activity Chart (full width) */}
      {sanitizedTimeline.length > 0 && (
        <div className="bg-white/[0.015] border border-white/[0.04] rounded-2xl p-6 mb-8 backdrop-blur-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-semibold text-gray-200 uppercase tracking-wider">
              {timePeriod === '24h' ? '24-Hour' : timePeriod === '7d' ? '7-Day' : timePeriod === '30d' ? '30-Day' : 'All-Time'} Activity
            </h3>
            <div className="flex items-center gap-3">
              {/* Time Period Selector */}
              <div className="flex items-center gap-2 bg-white/[0.04] rounded-lg p-1">
                <button
                  onClick={() => setTimePeriod('24h')}
                  className={`px-3 py-1.5 rounded transition-all text-xs font-medium ${
                    timePeriod === '24h' 
                      ? 'bg-white/[0.12] text-white' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  24H
                </button>
                <button
                  onClick={() => setTimePeriod('7d')}
                  className={`px-3 py-1.5 rounded transition-all text-xs font-medium ${
                    timePeriod === '7d' 
                      ? 'bg-white/[0.12] text-white' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  7D
                </button>
                <button
                  onClick={() => setTimePeriod('30d')}
                  className={`px-3 py-1.5 rounded transition-all text-xs font-medium ${
                    timePeriod === '30d' 
                      ? 'bg-white/[0.12] text-white' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  30D
                </button>
                <button
                  onClick={() => setTimePeriod('all')}
                  className={`px-3 py-1.5 rounded transition-all text-xs font-medium ${
                    timePeriod === 'all' 
                      ? 'bg-white/[0.12] text-white' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                >
                  ALL
                </button>
              </div>
              {/* Chart Type Selector */}
              <div className="flex items-center gap-2 bg-white/[0.04] rounded-lg p-1">
                <button
                  onClick={() => setChartType('bar')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded transition-all ${
                    chartType === 'bar' 
                      ? 'bg-white/[0.12] text-white' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                  title="Bar Chart"
                >
                  <BarChart3 className="w-4 h-4" />
                  <span className="text-xs font-medium">Bar</span>
                </button>
                <button
                  onClick={() => setChartType('area')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded transition-all ${
                    chartType === 'area' 
                      ? 'bg-white/[0.12] text-white' 
                      : 'text-gray-400 hover:text-gray-300'
                  }`}
                  title="Area Chart"
                >
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs font-medium">Area</span>
                </button>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={350}>
            {chartType === 'bar' ? (
              <BarChart 
                data={sanitizedTimeline}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.04)" />
                <XAxis 
                  dataKey="time" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#94a3b8', fontSize: 12, fontFamily: 'system-ui', fontWeight: 500 }} 
                  tickMargin={14}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 500 }} 
                  tickMargin={10}
                  width={45}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1a1d2e', 
                    border: '1px solid rgba(255,255,255,0.12)', 
                    borderRadius: '10px',
                    padding: '10px 14px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                  }} 
                  labelStyle={{ color: '#e2e8f0', fontSize: 12, marginBottom: '6px', fontWeight: 600 }}
                  itemStyle={{ color: '#f1f5f9', fontSize: 12, padding: '3px 0', fontWeight: 500 }}
                  cursor={{ fill: 'rgba(255,255,255,0.05)' }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="square"
                  formatter={(value) => <span style={{ color: '#d1d5db', fontSize: 12, fontWeight: 500 }}>{value}</span>}
                />
                {/* Stacked bars - order matters (bottom to top) */}
                <Bar 
                  dataKey="sessions" 
                  name="Sessions"
                  stackId="1"
                  fill="#06b6d4"
                  radius={[0, 0, 0, 0]}
                />
                <Bar 
                  dataKey="threats" 
                  name="Threats"
                  stackId="1"
                  fill="#a855f7"
                  radius={[0, 0, 0, 0]}
                />
                <Bar 
                  dataKey="deceptions" 
                  name="Deceptions"
                  stackId="1"
                  fill="#f59e0b"
                  radius={[0, 0, 0, 0]}
                />
                <Bar 
                  dataKey="fingerprints" 
                  name="Fingerprints"
                  stackId="1"
                  fill="#14b8a6"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            ) : (
              <AreaChart 
                data={sanitizedTimeline}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                stackOffset="none"
              >
                <defs>
                  <linearGradient id="colorSessionsStacked" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity={0.5} />
                  </linearGradient>
                  <linearGradient id="colorThreatsStacked" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a855f7" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity={0.5} />
                  </linearGradient>
                  <linearGradient id="colorDeceptionsStacked" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.5} />
                  </linearGradient>
                  <linearGradient id="colorFingerprintsStacked" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#14b8a6" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#14b8a6" stopOpacity={0.5} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.04)" />
                <XAxis 
                  dataKey="time" 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#94a3b8', fontSize: 12, fontFamily: 'system-ui', fontWeight: 500 }} 
                  tickMargin={14}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 500 }} 
                  tickMargin={10}
                  width={45}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1a1d2e', 
                    border: '1px solid rgba(255,255,255,0.12)', 
                    borderRadius: '10px',
                    padding: '10px 14px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                  }} 
                  labelStyle={{ color: '#e2e8f0', fontSize: 12, marginBottom: '6px', fontWeight: 600 }}
                  itemStyle={{ color: '#f1f5f9', fontSize: 12, padding: '3px 0', fontWeight: 500 }}
                  cursor={{ stroke: 'rgba(255,255,255,0.15)', strokeWidth: 2 }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="square"
                  formatter={(value) => <span style={{ color: '#d1d5db', fontSize: 12, fontWeight: 500 }}>{value}</span>}
                />
                {/* Stacked areas - order matters (bottom to top) */}
                <Area 
                  type="monotone" 
                  dataKey="sessions" 
                  name="Sessions"
                  stackId="1"
                  stroke="#06b6d4" 
                  strokeWidth={1.5} 
                  fill="url(#colorSessionsStacked)" 
                  dot={false}
                />
                <Area 
                  type="monotone" 
                  dataKey="threats" 
                  name="Threats"
                  stackId="1"
                  stroke="#a855f7" 
                  strokeWidth={1.5} 
                  fill="url(#colorThreatsStacked)" 
                  dot={false}
                />
                <Area 
                  type="monotone" 
                  dataKey="deceptions" 
                  name="Deceptions"
                  stackId="1"
                  stroke="#f59e0b" 
                  strokeWidth={1.5} 
                  fill="url(#colorDeceptionsStacked)" 
                  dot={false}
                />
                <Area 
                  type="monotone" 
                  dataKey="fingerprints" 
                  name="Fingerprints"
                  stackId="1"
                  stroke="#14b8a6" 
                  strokeWidth={1.5} 
                  fill="url(#colorFingerprintsStacked)" 
                  dot={false}
                />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
